/*
====================================
HELPERS
====================================
*/

const ARROW_KEYS = new Set(['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp']);
const FORWARD_ARROW_KEYS = new Set(['ArrowRight', 'ArrowDown']);
const CART_TRIGGER_SELECTOR = '.header__icon--cart, .set-item__add, .qs-add, .product-form__submit, .quick-add__submit';
const ACTIVE_CART_DRAWER_SELECTOR = 'cart-drawer.active, cart-drawer.drawer.animate.active';
const DRAWER_CLOSE_SELECTOR = '.drawer__close';
const FAQ_BUTTON_SELECTOR = '.faq-btn-wrap a[href^="#"]';
const QUICK_SHOP_TRIGGER_SELECTOR = '.quick-shop-btn';
const QUICK_SHOP_ADD_SELECTOR = '.qs-add';
const PRODUCT_FIRST_ACCORDION_SUMMARY_SELECTOR = '.product__accordion summary';
const PRODUCT_MEDIA_GALLERY_SELECTOR = '.product__media-list';
const ACCORDION_SCROLL_DOWN_OFFSET = 500;
const ACCORDION_SCROLL_UP_OFFSET = 120;
const ARROW_NAV_GROUPS = [
  ['#qs-colors', '.qs-color'],
  ['#qs-sizes', '.qs-size'],
  ['#qs-lengths', '.qs-length'],
  ['#qs-color-swatch', '.color-swatch'],
  ['#set-item-sizes', '.set-option'],
];

const focusLastTrigger = () => {
  setTimeout(() => {
    const trigger = cartDrawerReturnTrigger?.isConnected ? cartDrawerReturnTrigger : lastCartTrigger;

    trigger?.focus();
  }, 100);
};

const getClosest = (target, selector) => target.closest(selector);

const getHashTarget = (hash) => {
  if (!hash || hash === '#') return null;

  try {
    return document.querySelector(hash);
  } catch {
    return null;
  }
};

/*
====================================
LAST CART DRAWER TRIGGER
====================================
*/

let lastCartTrigger = null;
let lastQuickShopTrigger = null;
let cartDrawerReturnTrigger = null;
let accordionScrollTimeout = null;
let isTabNavigation = false;

const getProductMediaGallery = () => document.querySelector(PRODUCT_MEDIA_GALLERY_SELECTOR);

const scheduleAccordionScroll = (getTop) => {
  clearTimeout(accordionScrollTimeout);

  accordionScrollTimeout = setTimeout(() => {
    requestAnimationFrame(() => {
      const top = getTop();

      if (top === null) return;

      window.scrollTo({
        top,
        behavior: 'smooth',
      });
    });
  }, 150);
};

const scrollToFirstAccordionAfterGallery = () => {
  scheduleAccordionScroll(() => {
    const mediaGallery = getProductMediaGallery();

    if (!mediaGallery) return null;

    return mediaGallery.offsetTop + mediaGallery.offsetHeight - ACCORDION_SCROLL_DOWN_OFFSET;
  });
};

const scrollBackToMediaGallery = () => {
  scheduleAccordionScroll(() => {
    const mediaGallery = getProductMediaGallery();

    if (!mediaGallery) return null;

    return mediaGallery.offsetTop - ACCORDION_SCROLL_UP_OFFSET;
  });
};

/*
====================================
SCROLL TO FIRST ACCORDION AFTER MEDIA GALLERY
====================================
*/

document.addEventListener('focusin', (e) => {
  const quickShopTrigger = getClosest(e.target, QUICK_SHOP_TRIGGER_SELECTOR);

  if (quickShopTrigger) {
    lastQuickShopTrigger = quickShopTrigger;
  }

  const firstSummary = document.querySelector(PRODUCT_FIRST_ACCORDION_SUMMARY_SELECTOR);

  if (!firstSummary || e.target !== firstSummary || !isTabNavigation) return;

  scrollToFirstAccordionAfterGallery();
});

/*
====================================
STORE LAST TRIGGER
====================================
*/

document.addEventListener('click', (e) => {
  isTabNavigation = false;

  const quickShopTrigger = getClosest(e.target, QUICK_SHOP_TRIGGER_SELECTOR);

  if (quickShopTrigger) {
    lastQuickShopTrigger = quickShopTrigger;
  }

  const trigger = getClosest(e.target, CART_TRIGGER_SELECTOR);

  if (trigger) {
    lastCartTrigger = trigger;
    cartDrawerReturnTrigger = getClosest(trigger, QUICK_SHOP_ADD_SELECTOR) ? lastQuickShopTrigger : trigger;
  }

  /*
  ====================================
  CLOSE BUTTON CLICK
  ====================================
  */

  if (getClosest(e.target, DRAWER_CLOSE_SELECTOR)) {
    focusLastTrigger();
  }

  /*
  -----------------------------------
  FAQ BUTTON SCROLL + FOCUS SUMMARY
  -----------------------------------
  */

  const faqBtn = getClosest(e.target, FAQ_BUTTON_SELECTOR);

  if (!faqBtn) return;

  e.preventDefault();

  const targetEl = getHashTarget(faqBtn.getAttribute('href'));

  if (!targetEl) return;

  const firstSummary = targetEl.querySelector('summary');

  window.scrollTo({
    top: targetEl.getBoundingClientRect().top + window.pageYOffset - 120,
    behavior: 'smooth',
  });

  if (firstSummary) {
    firstSummary.setAttribute('tabindex', '-1');

    setTimeout(() => {
      firstSummary.focus();
    }, 400);
  }
});

/*
====================================
ESC CLOSE CART DRAWER
====================================
*/

document.addEventListener('keydown', (e) => {
  isTabNavigation = e.key === 'Tab';

  /*
  -----------------------------------
  SHIFT + TAB BACK TO GALLERY
  -----------------------------------
  */

  if (e.shiftKey && e.key === 'Tab') {
    const firstSummary = document.querySelector(PRODUCT_FIRST_ACCORDION_SUMMARY_SELECTOR);

    if (firstSummary && document.activeElement === firstSummary) {
      scrollBackToMediaGallery();
    }

    return;
  }

  /*
  -----------------------------------
  ESC CLOSE DRAWER
  -----------------------------------
  */

  if (e.key === 'Escape' || e.key === 'Esc') {
    const activeDrawer = document.querySelector(ACTIVE_CART_DRAWER_SELECTOR);

    if (!activeDrawer) return;

    e.preventDefault();

    const closeTarget =
      activeDrawer.querySelector(DRAWER_CLOSE_SELECTOR) || activeDrawer.querySelector('.cart-drawer-overlay');

    if (closeTarget) {
      closeTarget.click();
    } else {
      activeDrawer.classList.remove('active');
    }

    focusLastTrigger();

    return;
  }

  /*
  -----------------------------------
  ARROW NAVIGATION
  -----------------------------------
  */

  if (!ARROW_KEYS.has(e.key)) return;

  for (const [containerSelector, buttonSelector] of ARROW_NAV_GROUPS) {
    const currentButton = getClosest(e.target, `${containerSelector} ${buttonSelector}`);

    if (!currentButton) continue;

    e.preventDefault();

    const container = currentButton.closest(containerSelector);

    if (!container) return;

    const buttons = Array.from(container.querySelectorAll(`${buttonSelector}:not(:disabled)`));

    const currentIndex = buttons.indexOf(currentButton);

    if (currentIndex === -1) return;

    const direction = FORWARD_ARROW_KEYS.has(e.key) ? 1 : -1;

    const nextIndex = (currentIndex + direction + buttons.length) % buttons.length;

    const nextButton = buttons[nextIndex];

    nextButton?.focus();
    nextButton?.click();

    return;
  }
});

/*
====================================
VIDEO ACCESSIBILITY
====================================
*/

document.querySelectorAll('video').forEach((video) => {
  /*
   * ADD EMPTY CAPTION TRACK
   */
  if (!video.querySelector('track')) {
    const track = document.createElement('track');

    Object.assign(track, {
      kind: 'captions',
      label: 'English',
      srclang: 'en',
      src: "{{ 'empty.vtt' | asset_url }}",
    });

    video.appendChild(track);
  }

  /*
   * ADD ALT TO POSTER IMAGE
   */
  video.querySelectorAll('img').forEach((img) => {
    if (!img.hasAttribute('alt')) {
      img.setAttribute('alt', video.getAttribute('aria-label') || '');
    }
  });
});
